import { useEffect, useMemo, useState } from 'react';
import { Minus, Plus, RotateCcw, PackageOpen } from 'lucide-react';
import { useCollection } from '../hooks/useCollection';
import { useSocial } from '../hooks/useSocial';
import { computeTradeables } from '../utils/tradeables';
import { ensureCardsLoaded, getCardInfo } from '../utils/cardIndex';
import { getVariantConfig } from '../utils/cardVariants';

const FALLBACK_IMG =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA0OCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQ4IiBoZWlnaHQ9IjY0IiBmaWxsPSIjRjNGNEY2Ii8+CjxjaXJjbGUgY3g9IjI0IiBjeT0iMzIiIHI9IjEwIiBmaWxsPSIjRDFENURCIi8+Cjwvc3ZnPg==';

// "For Trade" tab: threshold stepper + the derived tradeable list with
// per-row exclude/undo controls. Quantities are computed client-side from
// collection + threshold + overrides (see utils/tradeables.js).
const TradeablesManager = () => {
  const { collection, settings, setSettings } = useCollection();
  const { tradeOverrides, tradeOverridesLoaded, setTradeOverride, clearTradeOverride } = useSocial();

  const threshold = Number.isInteger(settings.tradeThreshold) ? settings.tradeThreshold : 1;

  const rows = useMemo(
    () => computeTradeables(collection, tradeOverrides, threshold),
    [collection, tradeOverrides, threshold]
  );

  const offered = rows.filter((r) => r.tradeableQty > 0);
  const excluded = rows.filter((r) => r.override?.mode === 'exclude');
  const totalCopies = offered.reduce((sum, r) => sum + r.tradeableQty, 0);

  // Hydrate card names/images for the visible rows
  const [, setHydrated] = useState(0);
  useEffect(() => {
    const ids = rows.map((r) => r.cardId);
    if (ids.length === 0) return;
    let cancelled = false;
    ensureCardsLoaded(ids).then(() => {
      if (!cancelled) setHydrated((h) => h + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [rows]);

  const adjustThreshold = (delta) => {
    const next = Math.max(0, Math.min(99, threshold + delta));
    if (next !== threshold) setSettings({ tradeThreshold: next });
  };

  const Row = ({ row, isExcluded }) => {
    const info = getCardInfo(row.cardId);
    const config = getVariantConfig(row.variant);

    return (
      <div
        className={`bg-white border rounded-xl p-3 flex items-center gap-3 ${
          isExcluded ? 'border-border opacity-70' : 'border-border'
        }`}
      >
        <img
          src={info?.image || FALLBACK_IMG}
          alt={info?.name || row.cardId}
          className="w-10 h-14 object-contain rounded flex-shrink-0"
          onError={(e) => {
            e.target.src = FALLBACK_IMG;
          }}
        />

        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-ink-primary truncate">
            {info?.name || row.cardId}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span
              className={`${config.color} text-white text-[9px] font-bold rounded px-1 py-0.5`}
            >
              {config.short}
            </span>
            <span className="text-[11px] text-ink-secondary truncate">
              {info ? `${info.setName} #${info.number}` : row.variant}
            </span>
          </div>
        </div>

        {isExcluded ? (
          <>
            <span className="text-[11px] text-ink-muted flex-shrink-0">Kept back</span>
            <button
              onClick={() => clearTradeOverride(row.cardId, row.variant)}
              title="Offer for trade again"
              className="flex items-center gap-1 text-[12px] font-semibold text-navy-dark bg-cream-bg border border-border px-2.5 py-1.5 rounded-lg hover:bg-border/50 transition-colors flex-shrink-0"
            >
              <RotateCcw size={12} /> Undo
            </button>
          </>
        ) : (
          <>
            <div className="text-right flex-shrink-0">
              <div className="text-[13px] font-bold text-success-green">
                {row.tradeableQty} for trade
              </div>
              <div className="text-[11px] text-ink-muted">of {row.quantity} owned</div>
            </div>
            <button
              onClick={() => setTradeOverride(row.cardId, row.variant, 'exclude')}
              className="text-[12px] font-semibold text-ink-secondary bg-cream-bg border border-border px-2.5 py-1.5 rounded-lg hover:text-brick hover:bg-danger-bg hover:border-danger-border transition-colors flex-shrink-0"
            >
              Don't trade
            </button>
          </>
        )}
      </div>
    );
  };

  return (
    <div>
      {/* Threshold control */}
      <div className="bg-white border border-border rounded-2xl p-4 mb-5 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-ink-primary">Copies to keep</div>
          <div className="text-[12px] text-ink-secondary">
            Anything above this per version is automatically offered for trade
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => adjustThreshold(-1)}
            disabled={threshold === 0}
            className={`w-8 h-8 rounded-full flex items-center justify-center ${
              threshold === 0
                ? 'bg-border text-ink-faint cursor-not-allowed'
                : 'bg-danger-bg text-brick hover:bg-danger-border'
            }`}
          >
            <Minus size={14} />
          </button>
          <span className="w-8 text-center font-heading font-bold text-lg text-ink-primary">
            {threshold}
          </span>
          <button
            onClick={() => adjustThreshold(1)}
            className="w-8 h-8 rounded-full flex items-center justify-center bg-success-bg text-success-green hover:bg-success-border"
          >
            <Plus size={14} />
          </button>
        </div>
      </div>

      {!tradeOverridesLoaded ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-navy-dark border-t-transparent"></div>
        </div>
      ) : offered.length === 0 && excluded.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-14 text-center bg-white border border-border rounded-2xl">
          <div className="w-12 h-12 rounded-full bg-cream-bg border border-border flex items-center justify-center mb-3">
            <PackageOpen size={20} className="text-ink-faint" />
          </div>
          <p className="text-[13px] text-ink-secondary max-w-xs">
            No duplicates to trade yet. Cards with more than {threshold}{' '}
            {threshold === 1 ? 'copy' : 'copies'} of a version show up here automatically.
          </p>
        </div>
      ) : (
        <>
          {offered.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-[12px] font-semibold uppercase tracking-wide text-ink-muted">
                  Offered for trade
                </h3>
                <span className="text-[12px] text-ink-secondary">
                  {totalCopies} {totalCopies === 1 ? 'copy' : 'copies'} across {offered.length}{' '}
                  {offered.length === 1 ? 'card' : 'cards'}
                </span>
              </div>
              <div className="space-y-2">
                {offered.map((row) => (
                  <Row key={`${row.cardId}|${row.variant}`} row={row} isExcluded={false} />
                ))}
              </div>
            </div>
          )}

          {excluded.length > 0 && (
            <div>
              <h3 className="text-[12px] font-semibold uppercase tracking-wide text-ink-muted mb-2">
                Kept back
              </h3>
              <div className="space-y-2">
                {excluded.map((row) => (
                  <Row key={`${row.cardId}|${row.variant}`} row={row} isExcluded />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TradeablesManager;
